import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo } from 'react';
import { type StoreApi, useStore } from 'zustand';

import { type MostPopularBetsProps } from '../../contract';
import { createMostPopularBetsStore, type MostPopularBetsState, type SyncBet } from '../store';

type MostPopularBetsContextValue = {
  props: MostPopularBetsProps;
  store: StoreApi<MostPopularBetsState>;
};

const MostPopularBetsPropsContext = createContext<MostPopularBetsContextValue | null>(null);

export const MostPopularBetsPropsProvider = ({ children, ...props }: PropsWithChildren<MostPopularBetsProps>) => {
  const store = useMemo(() => createMostPopularBetsStore(), []);

  const removeAllSelectedBets = useCallback(() => {
    store.getState().removeAllSelectedBets();
  }, [store]);

  const removeSelectedBet = useCallback(
    (betId: number) => {
      store.getState().removeSelectedBet(betId);
    },
    [store],
  );

  const syncBetslip = useCallback(
    (bets: SyncBet[], overwriteSelections?: boolean) => {
      store.getState().syncBetslip?.(bets, overwriteSelections);
    },
    [store],
  );

  useEffect(() => {
    window.ocBetslip?.setCallbacks?.({
      clearBetslip: removeAllSelectedBets,
      removeBet: removeSelectedBet,
      syncBetslip,
    });
  }, [removeAllSelectedBets, removeSelectedBet, syncBetslip]);

  return (
    <MostPopularBetsPropsContext.Provider value={{ props, store }}>{children}</MostPopularBetsPropsContext.Provider>
  );
};

export const useMostPopularBetsProps = () => {
  const context = useContext(MostPopularBetsPropsContext);
  if (!context) {
    throw new Error('useMostPopularBetsProps must be used within MostPopularBetsPropsProvider');
  }
  return context.props;
};

export const useMostPopularBetsStore = <T,>(selector: (state: MostPopularBetsState) => T): T => {
  const context = useContext(MostPopularBetsPropsContext);
  if (!context) {
    throw new Error('useMostPopularBetsStore must be used within MostPopularBetsPropsProvider');
  }
  return useStore(context.store, selector);
};
